import { useTranslation as useI18nextTranslation } from 'react-i18next';
import type { LocaleCode, NamespaceKeys, TranslationHookReturn } from '../types';

const DEFAULT_LANGUAGE = 'pt-BR';
const SUPPORTED_LANGUAGES: LocaleCode[] = ['pt-BR', 'en-US'];
const LANGUAGE_STORAGE_KEY = 'cobli-language';

const normalizeLanguage = (language?: string): LocaleCode => (SUPPORTED_LANGUAGES.includes(language as LocaleCode) ? (language as LocaleCode) : DEFAULT_LANGUAGE);

/**
 * Enhanced useTranslation hook with type safety and Cobli-specific features
 * @param namespace - The translation namespace to use
 * @returns Translation utilities and functions
 */
export function useTranslation(namespace?: NamespaceKeys | NamespaceKeys[]): TranslationHookReturn {
  const { t, i18n } = useI18nextTranslation(namespace);

  return {
    t,
    language: normalizeLanguage(i18n.resolvedLanguage ?? i18n.language),
    isReady: i18n.isInitialized,
  };
}

/**
 * Hook for getting current language information
 */
export function useLanguage() {
  const { i18n } = useI18nextTranslation();
  const currentLanguage = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);

  return {
    currentLanguage,
    isPortuguese: currentLanguage === 'pt-BR',
    isEnglish: currentLanguage === 'en-US',
    googleMapsLanguage: currentLanguage,
    changeLanguage: async (language?: string) => {
      const nextLanguage = normalizeLanguage(language);
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
        } catch {
          // localStorage blocked/unavailable; proceed without persistence
        }
      }
      await i18n.changeLanguage(nextLanguage);
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    },
  };
}
